/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CollectionModelUserRoleResponseModel } from '../models/collection-model-user-role-response-model';
import { GetMerchantAdminUsersResponse } from '../models/get-merchant-admin-users-response';
import { PagedModelUserResponseModel } from '../models/paged-model-user-response-model';
import { PostMerchantAdminUserRequest } from '../models/post-merchant-admin-user-request';
import { PostMerchantRootAdminUserRequest } from '../models/post-merchant-root-admin-user-request';
import { PutMerchantAdminUserRequest } from '../models/put-merchant-admin-user-request';
import { User } from '../models/user';
import { UserResponseModel } from '../models/user-response-model';
import { UserRoleResponseModel } from '../models/user-role-response-model';

/**
 * Users endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class UsersService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getRoles
   */
  static readonly GetRolesPath = '/users/{userId}/roles';

  /**
   * Listing roles.
   *
   * Lists all roles of specified user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRoles()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRoles$Response(
    params: {
      userId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CollectionModelUserRoleResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.GetRolesPath,
      'get'
    );
    if (params) {
      rb.path('userId', params.userId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CollectionModelUserRoleResponseModel>;
        })
      );
  }

  /**
   * Listing roles.
   *
   * Lists all roles of specified user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRoles$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRoles(
    params: {
      userId: string;
    },
    context?: HttpContext
  ): Observable<CollectionModelUserRoleResponseModel> {
    return this.getRoles$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<CollectionModelUserRoleResponseModel>) =>
          r.body as CollectionModelUserRoleResponseModel
      )
    );
  }

  /**
   * Path part for operation setRolesById
   */
  static readonly SetRolesByIdPath = '/users/{userId}/roles';

  /**
   * Setting roles.
   *
   * Sets roles of specified user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `setRolesById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  setRolesById$Response(
    params: {
      userId: string;
      body: Array<
        | 'PRODUCT_ADMIN'
        | 'PRICING_ADMIN'
        | 'MERCHANT_ADMIN'
        | 'COMMERCIAL_ADMIN'
        | 'EXCISE_MANAGER'
        | 'SUBSCRIPTION_ADMIN'
        | 'SETTINGS_ADMIN'
        | 'USER_ADMIN'
        | 'SUPPLY_MANAGER'
        | 'EASYLOT_ADMIN'
        | 'TELESALES'
        | 'TECHNICAL_USER'
        | 'IMPERSONATION'
      >;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.SetRolesByIdPath,
      'put'
    );
    if (params) {
      rb.path('userId', params.userId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Setting roles.
   *
   * Sets roles of specified user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `setRolesById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  setRolesById(
    params: {
      userId: string;
      body: Array<
        | 'PRODUCT_ADMIN'
        | 'PRICING_ADMIN'
        | 'MERCHANT_ADMIN'
        | 'COMMERCIAL_ADMIN'
        | 'EXCISE_MANAGER'
        | 'SUBSCRIPTION_ADMIN'
        | 'SETTINGS_ADMIN'
        | 'USER_ADMIN'
        | 'SUPPLY_MANAGER'
        | 'EASYLOT_ADMIN'
        | 'TELESALES'
        | 'TECHNICAL_USER'
        | 'IMPERSONATION'
      >;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.setRolesById$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation revokeRoles
   */
  static readonly RevokeRolesPath = '/users/{userId}/roles';

  /**
   * Setting roles.
   *
   * Revoke roles of specified user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `revokeRoles()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  revokeRoles$Response(
    params: {
      userId: string;
      body: Array<
        | 'PRODUCT_ADMIN'
        | 'PRICING_ADMIN'
        | 'MERCHANT_ADMIN'
        | 'COMMERCIAL_ADMIN'
        | 'EXCISE_MANAGER'
        | 'SUBSCRIPTION_ADMIN'
        | 'SETTINGS_ADMIN'
        | 'USER_ADMIN'
        | 'SUPPLY_MANAGER'
        | 'EASYLOT_ADMIN'
        | 'TELESALES'
        | 'TECHNICAL_USER'
        | 'IMPERSONATION'
      >;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.RevokeRolesPath,
      'delete'
    );
    if (params) {
      rb.path('userId', params.userId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Setting roles.
   *
   * Revoke roles of specified user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `revokeRoles$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  revokeRoles(
    params: {
      userId: string;
      body: Array<
        | 'PRODUCT_ADMIN'
        | 'PRICING_ADMIN'
        | 'MERCHANT_ADMIN'
        | 'COMMERCIAL_ADMIN'
        | 'EXCISE_MANAGER'
        | 'SUBSCRIPTION_ADMIN'
        | 'SETTINGS_ADMIN'
        | 'USER_ADMIN'
        | 'SUPPLY_MANAGER'
        | 'EASYLOT_ADMIN'
        | 'TELESALES'
        | 'TECHNICAL_USER'
        | 'IMPERSONATION'
      >;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.revokeRoles$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation assignRoles
   */
  static readonly AssignRolesPath = '/users/{userId}/roles';

  /**
   * Assign roles.
   *
   * Assigns roles of specified user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `assignRoles()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  assignRoles$Response(
    params: {
      userId: string;
      body: Array<
        | 'PRODUCT_ADMIN'
        | 'PRICING_ADMIN'
        | 'MERCHANT_ADMIN'
        | 'COMMERCIAL_ADMIN'
        | 'EXCISE_MANAGER'
        | 'SUBSCRIPTION_ADMIN'
        | 'SETTINGS_ADMIN'
        | 'USER_ADMIN'
        | 'SUPPLY_MANAGER'
        | 'EASYLOT_ADMIN'
        | 'TELESALES'
        | 'TECHNICAL_USER'
        | 'IMPERSONATION'
      >;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CollectionModelUserRoleResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.AssignRolesPath,
      'patch'
    );
    if (params) {
      rb.path('userId', params.userId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CollectionModelUserRoleResponseModel>;
        })
      );
  }

  /**
   * Assign roles.
   *
   * Assigns roles of specified user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `assignRoles$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  assignRoles(
    params: {
      userId: string;
      body: Array<
        | 'PRODUCT_ADMIN'
        | 'PRICING_ADMIN'
        | 'MERCHANT_ADMIN'
        | 'COMMERCIAL_ADMIN'
        | 'EXCISE_MANAGER'
        | 'SUBSCRIPTION_ADMIN'
        | 'SETTINGS_ADMIN'
        | 'USER_ADMIN'
        | 'SUPPLY_MANAGER'
        | 'EASYLOT_ADMIN'
        | 'TELESALES'
        | 'TECHNICAL_USER'
        | 'IMPERSONATION'
      >;
    },
    context?: HttpContext
  ): Observable<CollectionModelUserRoleResponseModel> {
    return this.assignRoles$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<CollectionModelUserRoleResponseModel>) =>
          r.body as CollectionModelUserRoleResponseModel
      )
    );
  }

  /**
   * Path part for operation getRoleById
   */
  static readonly GetRoleByIdPath = '/users/{userId}/roles/{roleName}';

  /**
   * Listing roles.
   *
   * Lists all roles of specified user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRoleById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRoleById$Response(
    params: {
      userId: string;
      roleName:
        | 'PRODUCT_ADMIN'
        | 'PRICING_ADMIN'
        | 'MERCHANT_ADMIN'
        | 'COMMERCIAL_ADMIN'
        | 'EXCISE_MANAGER'
        | 'SUBSCRIPTION_ADMIN'
        | 'SETTINGS_ADMIN'
        | 'USER_ADMIN'
        | 'SUPPLY_MANAGER'
        | 'EASYLOT_ADMIN'
        | 'TELESALES'
        | 'TECHNICAL_USER'
        | 'IMPERSONATION';
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserRoleResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.GetRoleByIdPath,
      'get'
    );
    if (params) {
      rb.path('userId', params.userId, {});
      rb.path('roleName', params.roleName, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserRoleResponseModel>;
        })
      );
  }

  /**
   * Listing roles.
   *
   * Lists all roles of specified user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRoleById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRoleById(
    params: {
      userId: string;
      roleName:
        | 'PRODUCT_ADMIN'
        | 'PRICING_ADMIN'
        | 'MERCHANT_ADMIN'
        | 'COMMERCIAL_ADMIN'
        | 'EXCISE_MANAGER'
        | 'SUBSCRIPTION_ADMIN'
        | 'SETTINGS_ADMIN'
        | 'USER_ADMIN'
        | 'SUPPLY_MANAGER'
        | 'EASYLOT_ADMIN'
        | 'TELESALES'
        | 'TECHNICAL_USER'
        | 'IMPERSONATION';
    },
    context?: HttpContext
  ): Observable<UserRoleResponseModel> {
    return this.getRoleById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<UserRoleResponseModel>) =>
          r.body as UserRoleResponseModel
      )
    );
  }

  /**
   * Path part for operation assignRole
   */
  static readonly AssignRolePath = '/users/{userId}/roles/{roleName}';

  /**
   * Assign role.
   *
   * Assigns the given role to specified user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `assignRole()` instead.
   *
   * This method doesn't expect any request body.
   */
  assignRole$Response(
    params: {
      userId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserRoleResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.AssignRolePath,
      'put'
    );
    if (params) {
      rb.path('userId', params.userId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserRoleResponseModel>;
        })
      );
  }

  /**
   * Assign role.
   *
   * Assigns the given role to specified user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `assignRole$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  assignRole(
    params: {
      userId: string;
    },
    context?: HttpContext
  ): Observable<UserRoleResponseModel> {
    return this.assignRole$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<UserRoleResponseModel>) =>
          r.body as UserRoleResponseModel
      )
    );
  }

  /**
   * Path part for operation revokeRole
   */
  static readonly RevokeRolePath = '/users/{userId}/roles/{roleName}';

  /**
   * Revoke role.
   *
   * Revokes the given role from specified user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `revokeRole()` instead.
   *
   * This method doesn't expect any request body.
   */
  revokeRole$Response(
    params: {
      userId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.RevokeRolePath,
      'delete'
    );
    if (params) {
      rb.path('userId', params.userId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Revoke role.
   *
   * Revokes the given role from specified user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `revokeRole$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  revokeRole(
    params: {
      userId: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.revokeRole$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateUser
   */
  static readonly UpdateUserPath = '/users/merchant-admin-users/{userId}';

  /**
   * Updating existing merchant user admin.
   *
   * Updates an existing merchant admin user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateUser()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateUser$Response(
    params: {
      userId: string;
      body: PutMerchantAdminUserRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.UpdateUserPath,
      'put'
    );
    if (params) {
      rb.path('userId', params.userId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updating existing merchant user admin.
   *
   * Updates an existing merchant admin user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateUser$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateUser(
    params: {
      userId: string;
      body: PutMerchantAdminUserRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateUser$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteUser
   */
  static readonly DeleteUserPath = '/users/merchant-admin-users/{userId}';

  /**
   * Deleting existing merchant user admin.
   *
   * Deletes an existing merchant admin user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUser$Response(
    params: {
      userId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.DeleteUserPath,
      'delete'
    );
    if (params) {
      rb.path('userId', params.userId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Deleting existing merchant user admin.
   *
   * Deletes an existing merchant admin user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUser(
    params: {
      userId: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteUser$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation registerMerchantRootUser
   */
  static readonly RegisterMerchantRootUserPath =
    '/users/merchant-root-admin-users';

  /**
   * Registering new merchant root admin.
   *
   * Registers a new merchant root admin and returns the saved resource with its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `registerMerchantRootUser()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  registerMerchantRootUser$Response(
    params: {
      body: PostMerchantRootAdminUserRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<User>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.RegisterMerchantRootUserPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<User>;
        })
      );
  }

  /**
   * Registering new merchant root admin.
   *
   * Registers a new merchant root admin and returns the saved resource with its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `registerMerchantRootUser$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  registerMerchantRootUser(
    params: {
      body: PostMerchantRootAdminUserRequest;
    },
    context?: HttpContext
  ): Observable<User> {
    return this.registerMerchantRootUser$Response(params, context).pipe(
      map((r: StrictHttpResponse<User>) => r.body as User)
    );
  }

  /**
   * Path part for operation listMerchantUsers
   */
  static readonly ListMerchantUsersPath = '/users/merchant-admin-users';

  /**
   * Listing merchant admin users.
   *
   * Lists all the merchant admin users. The merchant is specified by the access token.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listMerchantUsers()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMerchantUsers$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetMerchantAdminUsersResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.ListMerchantUsersPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetMerchantAdminUsersResponse>;
        })
      );
  }

  /**
   * Listing merchant admin users.
   *
   * Lists all the merchant admin users. The merchant is specified by the access token.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listMerchantUsers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMerchantUsers(
    params?: {},
    context?: HttpContext
  ): Observable<GetMerchantAdminUsersResponse> {
    return this.listMerchantUsers$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetMerchantAdminUsersResponse>) =>
          r.body as GetMerchantAdminUsersResponse
      )
    );
  }

  /**
   * Path part for operation addUserToMerchant
   */
  static readonly AddUserToMerchantPath = '/users/merchant-admin-users';

  /**
   * Registering new merchant user admin.
   *
   * Registers a new merchant admin user and returns the saved resource with its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addUserToMerchant()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addUserToMerchant$Response(
    params: {
      body: PostMerchantAdminUserRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<User>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.AddUserToMerchantPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<User>;
        })
      );
  }

  /**
   * Registering new merchant user admin.
   *
   * Registers a new merchant admin user and returns the saved resource with its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addUserToMerchant$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addUserToMerchant(
    params: {
      body: PostMerchantAdminUserRequest;
    },
    context?: HttpContext
  ): Observable<User> {
    return this.addUserToMerchant$Response(params, context).pipe(
      map((r: StrictHttpResponse<User>) => r.body as User)
    );
  }

  /**
   * Path part for operation listUsers
   */
  static readonly ListUsersPath = '/users';

  /**
   * Listing users.
   *
   * Lists all users.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listUsers()` instead.
   *
   * This method doesn't expect any request body.
   */
  listUsers$Response(
    params?: {
      merchantId?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelUserResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.ListUsersPath,
      'get'
    );
    if (params) {
      rb.query('merchantId', params.merchantId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelUserResponseModel>;
        })
      );
  }

  /**
   * Listing users.
   *
   * Lists all users.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listUsers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listUsers(
    params?: {
      merchantId?: string;
    },
    context?: HttpContext
  ): Observable<PagedModelUserResponseModel> {
    return this.listUsers$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagedModelUserResponseModel>) =>
          r.body as PagedModelUserResponseModel
      )
    );
  }

  /**
   * Path part for operation getUser
   */
  static readonly GetUserPath = '/users/{userId}';

  /**
   * Get a user.
   *
   * Returns the specified user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUser$Response(
    params: {
      userId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.GetUserPath,
      'get'
    );
    if (params) {
      rb.path('userId', params.userId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserResponseModel>;
        })
      );
  }

  /**
   * Get a user.
   *
   * Returns the specified user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUser(
    params: {
      userId: string;
    },
    context?: HttpContext
  ): Observable<UserResponseModel> {
    return this.getUser$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<UserResponseModel>) =>
          r.body as UserResponseModel
      )
    );
  }
}
